/**
 * @namespace layout/navigation
 */
import { STORE_PREFIX as LAYOUT_STORE_PREFIX, ERRORS as LAYOUT_ERRORS } from '~/utils/lib/layout/types'
import storage from '~/utils/lib/core/storage'
import { Maybe, not, notEmpty, propOr } from '~/utils/lib/core/prelude'
import { findCompany, findStructure, useStore_client_access } from '~/stores/client/access'

/**
 * @desc
 *    Navigation state representation, see navigation scope guide for
 *    more informations
 *
 *    TL;DR:
 *      - navigation perimeter describes the current navigation regarding the
 *        the section navigation scope (static information stored in the
 *        page or parent page)
 * @typedef {NavigationPerimeter} NavigationState
 */
/**
 * @typedef {{
 *    structure_id?: import('~/stores/client/access').Structure["id"];
 *    company_code?: import('~/stores/client/access').Company["code_societe"];
 * }} NavigationPerimeter
 */

/** @type {NavigationPerimeter} */
export const navigationPerimeterBoilerplate = {
  structure_id: undefined,
  company_code: undefined,
}

/** ================================================================ **/

export function resetNavigationStorage() {
  storage.delete(LAYOUT_STORE_PREFIX, 'structure')
  storage.delete(LAYOUT_STORE_PREFIX, 'company')
}

export function storeNavigationStructure(
  /** @type {import('~/stores/client/access/index').Structure["id"]} */ id
) {
  storage.set(LAYOUT_STORE_PREFIX, 'structure', id)
}

export function storeNavigationCompany(
  /** @type {import('~/stores/client/access/index').Company["code_societe"]} */ code_societe
) {
  storage.set(LAYOUT_STORE_PREFIX, 'company', code_societe)
}

/** @type {() => Promise<import('~/utils/lib/core/prelude_').Maybe<import('~/stores/client/access/index').Structure["id"]>>} */
export const getLastSelectedStructure = () => storage
  .get(LAYOUT_STORE_PREFIX, 'structure')
  .then(Maybe.toMaybe)

/** @type {() => Promise<import('~/utils/lib/core/prelude_').Maybe<import('~/stores/client/access/index').Company["code_societe"]>>} */
export const getLastSelectedCompany = () => storage
  .get(LAYOUT_STORE_PREFIX, 'company')
  .then(Maybe.toMaybe)

/** ================================================================ **/

export const useStore_layout_navigation = defineStore('layout/navigation', {
  state: () => ({
    isCompanySidebarVisible: false,
  }),
  actions: {
    /// Mutations
    ///
    setCompanySidebarVisibility(/** @type {boolean} */ flag) {
      this.isCompanySidebarVisible = flag
    },
    toggleCompanySidebarVisibility() {
      this.isCompanySidebarVisible = not(this.isCompanySidebarVisible)
    },
  }
})
/*
=====================================================
*/
/**
 * @param {import('~/utils/lib/core/types_').Route_} route
 * @returns {import('~/stores/client/access').Company["code_societe"]|""}
 */
export function extractRouteCompanyCode(route) {
  const companyFromQuery = propOr('', 'company', route.query ?? {})
  const companyFromQueryString = Array.isArray(companyFromQuery) ?
    (companyFromQuery[0] ?? '') :
    companyFromQuery

  if (companyFromQueryString)
    return companyFromQueryString

  return ''
}
/**
 * @param {NavigationPerimeter} _
 * @returns {boolean}
 */
export function isCompanyCodeValidForStructure(
  {
    structure_id = '',
    company_code = '',
  }
) {
  if (!notEmpty(structure_id) || !notEmpty(company_code))
    return false

  const accessData = useStore_client_access().data
  const mStructure = findStructure(accessData, structure_id)

  if (Maybe.isNothing(mStructure))
    return false

  const structure = Maybe.fromJust(mStructure)
  const mCompany = findCompany(structure, company_code)

  return Maybe.isJust(mCompany)
}
/**
 * @param {import('~/utils/lib/core/types_').Route_} route
 * @returns {string}
 */
export function stripCompanyQueryFromRoute(route) {
  const [pathWithQuery = '', hash = ''] = String(route.fullPath ?? '').split('#')
  const [path = '', search = ''] = pathWithQuery.split('?')
  const query = new URLSearchParams(search)

  query.delete('company')

  const searchString = query.toString()

  return [
    path,
    searchString ? `?${searchString}` : '',
    hash ? `#${hash}` : '',
  ].join('')
}
/**
 * @desc Extract navigation perimeter from a route
 * @NOTE since "nuxt@4.2" cannot reuse the top-level package "vue-router",
 *       hence the odd typing
 * @param {import('~/utils/lib/core/types_').Route_} route
 * @return {{
 *  structure_id: import('~/stores/client/access').Structure["id"]|"";
 *  company_code: import('~/stores/client/access').Company["code_societe"] |"";
 * }}
 */
export function extractRouteLevelRecord(route) {
  const structure_id = propOr('', 'dataId', route.params)
  const company_code = extractRouteCompanyCode(route)

  return {
    structure_id,
    company_code,
  }
}
/**
 * @param {import('~/utils/lib/core/types_').Route_} route
 * @returns {import('~/utils/lib/layout/types').NAVIGATION_SCOPE|undefined}
 */
export function extractRouteNavigationScope(route) {
  /** @type {import('~/utils/lib/layout/types').NAVIGATION_SCOPE|undefined} */
  // @ts-ignore: UNSAFE coerce
  return route?.meta?.navigationScope
}
/**
 * @param {import('~/utils/lib/core/types_').Route_} route
 * @returns {import('~/utils/lib/layout/types').MENU_ITEM|undefined}
 */
export function extractRouteMenuItem(route) {
  /** @type {import('~/utils/lib/layout/types').MENU_ITEM|undefined} */
  // @ts-ignore: UNSAFE coerce
  return route?.meta?.menuItem
}
/*
=====================================================
*/
/**
 * @desc
 *    Returns `Structure` information
 *
 *    Throws when the current perimeter does not resolve to a known structure
 * @throws {Error}
 * @param {import('~/stores/layout/navigation').NavigationPerimeter} _
 * @return {{
 *  structure: import('~/stores/client/access').Structure;
 * }}
 */
export function fromStructureLevel(
  {
    structure_id = '',
  }
) {
  const accessData = useStore_client_access().data
  const mStructure = findStructure(accessData, structure_id)

  if (Maybe.isNothing(mStructure))
    throw new Error(LAYOUT_ERRORS.UNKNOWN_STRUCTURE)

  const structure = Maybe.fromJust(mStructure)

  return { structure }
}
/**
 * @desc
 *    Returns both `Structure` and `Company` information
 *
 *    Throws when the current perimeter does not resolve to a known company
 * @throws {Error}
 * @param {import('~/stores/layout/navigation').NavigationPerimeter} _
 * @return {{
 *  structure: import('~/stores/client/access').Structure;
 *  company: import('~/stores/client/access').Company;
 * }}
 */
export function fromCompanyLevel(
  {
    structure_id = '',
    company_code = '',
  }
) {
  const accessData = useStore_client_access().data
  const mStructure = findStructure(accessData, structure_id)

  if (Maybe.isNothing(mStructure))
    throw new Error(LAYOUT_ERRORS.UNKNOWN_STRUCTURE)

  const structure = Maybe.fromJust(mStructure)
  const mCompany = findCompany(structure, company_code)

  if (Maybe.isNothing(mCompany))
    throw new Error(LAYOUT_ERRORS.UNKNOWN_COMPANY)

  const company = Maybe.fromJust(mCompany)

  return { structure, company }
}
/**
 * @desc
 *    Returns current narrowed perimeter:
 *      - `COMPANY` when both structure and company are valid
 *      - `STRUCTURE` when only structure is valid
 *
 *    This helper is meant for `COMPANY_OPTIONAL` pages.
 * @param {import('~/stores/layout/navigation').NavigationPerimeter} _
 * @return {{
 *  kind: 'COMPANY';
 *  structure: import('~/stores/client/access').Structure;
 *  company: import('~/stores/client/access').Company;
 * }|{
 *  kind: 'STRUCTURE';
 *  structure: import('~/stores/client/access').Structure;
 * }}
 */
export function fromCurrentLevel(
  {
    structure_id = '',
    company_code = '',
  }
) {
  const accessData = useStore_client_access().data
  const mStructure = findStructure(accessData, structure_id)

  if (Maybe.isNothing(mStructure))
    throw new Error(LAYOUT_ERRORS.UNKNOWN_STRUCTURE)

  const structure = Maybe.fromJust(mStructure)

  if (company_code) {
    const mCompany = findCompany(structure, company_code)

    if (Maybe.isJust(mCompany))
      return {
        kind: 'COMPANY',
        structure,
        company: Maybe.fromJust(mCompany),
      }
  }

  return {
    kind: 'STRUCTURE',
    structure,
  }
}
